import { Modal } from "components/modal/modal";
import { ModalSize } from "components/modal/modal.types";
import { SubjectTag } from "components/subject-tag/subject-tag";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { colors } from "styles/constants/colors";
import { GuruJuaraBadge } from "../assets/guru-juara-badge";
import {
  didStudentAttendClass,
  formatTimeStamp,
  getTimeStampFromDateString,
  getVideoMaterialsForClass
} from "../utils";
import {
  PastClassDetailAttendance,
  PastClassDetailAttendanceMessage,
  PastClassDetailBlock,
  PastClassDetailDescriptionBlock,
  PastClassDetailDescriptionBlockDesc,
  PastClassDetailDescriptionBlockHeading,
  PastClassDetailHeading,
  PastClassDetailInfoItem,
  PastClassDetailInfoItemContainer,
  PastClassDetailMetaBlock,
  PastClassDetailTeacherName,
  PastClassDetailVideoBlock,
  PastClassDetailVideoBlockBackButton,
  PastClassDetailVideoBlockTitle,
  PastClassDetailVideoBlockWrapper,
  PastClassModalTopicsContainer
} from "./past-class-detail-modal.styles";
import { IPastClassDetailModalProps } from "./past-class-detail-modal.types";
import {
  ClassSessionVideoMaterial,
  SubscriptionType
} from "services/api/sessions/sessionService.types";
import ReactPlayer from "react-player";
import { downloadFile, getIndonesianTimezone, getPageName } from "utils/utils";
import { useToast } from "components/atoms/toast/toast-context";
import { SingleClassMaterialBlock } from "./single-class-material-block";
import { MultipleClassMaterialBlock } from "./multiple-class-material-block";
import { Spacer } from "styles/common/spacer";
import { SubjectTagData } from "components/subject-tag/subject-tag.data";
import trackEvent from "services/mixpanel/mixpanel";
import { kelasLiveEvents } from "services/mixpanel/kelas-live-events";
import { BackBtn } from "components/icons/back-btn";
import { CheckCircle } from "components/icons/check-circle";
import { InfoIcon } from "components/icons/info";
import { Play } from "components/icons/play";
import * as Sentry from "@sentry/nextjs";

const PastClassDetailModal: React.FC<IPastClassDetailModalProps> = ({
  classSession,
  baseMediaURL,
  handleClose,
  subscriptionType,
  primaryUser,
  subscriptionStatus
}) => {
  const videoMaterials = getVideoMaterialsForClass(classSession);
  const classAttended = didStudentAttendClass(classSession);

  const [currentDownloadingFile, setCurrentDownloadingFile] = useState<
    string | null
  >(null);
  const { addToast } = useToast();

  const [selectedVideo, setSelectedVideo] =
    useState<ClassSessionVideoMaterial | null>(null);

  useEffect(() => {
    const pageName = getPageName();

    trackEvent({
      eventName: kelasLiveEvents.pastClassDetailModalViewed,
      payload: {
        userType: subscriptionType,
        grade: primaryUser.grade,
        curriculum: primaryUser.curriculum,
        stream: primaryUser.stream,
        incomingSource: pageName
      }
    });
  }, []);

  const onDuration = (videoDuration: number) => {
    trackEvent({
      eventName: kelasLiveEvents.viewReviewVideo,
      payload: {
        videoDuration,
        grade: primaryUser?.grade,
        userType: subscriptionStatus.subscriptionType
      }
    });
  };

  const downloadStudyMaterial = async (url: string, fileName: string) => {
    trackEvent({
      eventName: kelasLiveEvents.downloadClassMaterialPressed
    });
    setCurrentDownloadingFile(url);
    try {
      await downloadFile(url, fileName);
    } catch (err) {
      Sentry.captureException(err);
      setCurrentDownloadingFile(null);
      addToast({ heading: "Failed to download, try again", variant: "error" });
    }
    setCurrentDownloadingFile(null);
  };
  const subjectTagData = SubjectTagData[classSession.subject.toUpperCase()];

  const canAccessStudyMaterial =
    subscriptionType === SubscriptionType.FREE_AFTER_TRIAL ||
    subscriptionType === SubscriptionType.FREE_AFTER_PAID ||
    subscriptionType === SubscriptionType.FREE ||
    subscriptionType === SubscriptionType.PAUSED
      ? false
      : true;

  const renderClassMaterialBlock = () => {
    const combinedClassMaterials = [
      ...videoMaterials,
      ...(classSession.studyMaterialUrls || [])
    ];

    if (combinedClassMaterials.length === 1) {
      return (
        <SingleClassMaterialBlock
          videoMaterials={videoMaterials}
          baseMediaURL={baseMediaURL}
          classSession={classSession}
          currentDownloadingFile={currentDownloadingFile}
          setSelectedVideo={setSelectedVideo}
          downloadStudyMaterial={downloadStudyMaterial}
          canAccessStudyMaterial={canAccessStudyMaterial}
          subscriptionStatus={subscriptionStatus}
          primaryUser={primaryUser}
        />
      );
    }
    if (combinedClassMaterials.length > 1) {
      return (
        <MultipleClassMaterialBlock
          videoMaterials={videoMaterials}
          baseMediaURL={baseMediaURL}
          classSession={classSession}
          currentDownloadingFile={currentDownloadingFile}
          setSelectedVideo={setSelectedVideo}
          downloadStudyMaterial={downloadStudyMaterial}
          canAccessStudyMaterial={canAccessStudyMaterial}
          subscriptionStatus={subscriptionStatus}
          primaryUser={primaryUser}
        />
      );
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        handleClose();
      }}
      closeOnClickOutside={false}
      size={ModalSize.Wide}
    >
      {!selectedVideo && (
        <div>
          <PastClassModalTopicsContainer>
            <li>
              <SubjectTag
                backgroundColor={subjectTagData?.backgroundColor}
                color={subjectTagData?.color}
                text={subjectTagData?.text}
                iconSrc={subjectTagData?.iconSrc}
              />
            </li>
            {classSession.tags?.map((tag, idx) => (
              <li key={idx}>
                <SubjectTag
                  backgroundColor={colors.periwinkle[200]}
                  color={colors.periwinkle[700]}
                  text={tag}
                />
              </li>
            ))}
          </PastClassModalTopicsContainer>
          <PastClassDetailHeading>{classSession.topic}</PastClassDetailHeading>
          <PastClassDetailMetaBlock>
            <PastClassDetailBlock>
              <PastClassDetailTeacherName>
                {classSession.teacher.name} <GuruJuaraBadge />
              </PastClassDetailTeacherName>
              <PastClassDetailInfoItemContainer>
                <PastClassDetailInfoItem>
                  {formatTimeStamp(classSession.startDate)}
                </PastClassDetailInfoItem>
                <PastClassDetailInfoItem>
                  {format(
                    getTimeStampFromDateString(classSession.startDate),
                    "k:mm"
                  )}{" "}
                  {getIndonesianTimezone()}
                </PastClassDetailInfoItem>
                <PastClassDetailInfoItem>
                  {classSession.duration} menit
                </PastClassDetailInfoItem>
              </PastClassDetailInfoItemContainer>
            </PastClassDetailBlock>
            <PastClassDetailAttendance classAttended={classAttended}>
              {classAttended ? (
                <PastClassDetailAttendanceMessage>
                  <CheckCircle /> Hadir
                </PastClassDetailAttendanceMessage>
              ) : (
                <PastClassDetailAttendanceMessage>
                  <InfoIcon /> Tidak hadir
                </PastClassDetailAttendanceMessage>
              )}
            </PastClassDetailAttendance>
          </PastClassDetailMetaBlock>
          {renderClassMaterialBlock()}
          <Spacer token={[16, 16, 32]} />
          <PastClassDetailDescriptionBlock>
            <PastClassDetailDescriptionBlockHeading>
              CoFriends akan belajar
            </PastClassDetailDescriptionBlockHeading>
            <PastClassDetailDescriptionBlockDesc
              dangerouslySetInnerHTML={{ __html: classSession.description }}
            />
          </PastClassDetailDescriptionBlock>
        </div>
      )}
      {selectedVideo && (
        <>
          <PastClassDetailVideoBlock>
            <PastClassDetailVideoBlockBackButton
              onClick={() => setSelectedVideo(null)}
            >
              <BackBtn />
              Balik ke detail kelas
            </PastClassDetailVideoBlockBackButton>
            <PastClassDetailVideoBlockTitle>
              {classSession.topic}
            </PastClassDetailVideoBlockTitle>
            <PastClassDetailVideoBlockWrapper>
              <ReactPlayer
                width="100%"
                height="100%"
                style={{
                  position: "absolute",
                  top: "0",
                  left: "0",
                  right: "0",
                  bottom: "0"
                }}
                onDuration={onDuration}
                controls={true}
                playing={true}
                muted={true}
                url={`${baseMediaURL}/${selectedVideo.url}`}
                pip={false}
                config={{
                  file: {
                    hlsOptions: {
                      startLevel: -1,
                      forceHLS: true,
                      debug: false
                    }
                  }
                }}
                playIcon={
                  <div>
                    <Play />
                  </div>
                }
              />
            </PastClassDetailVideoBlockWrapper>
          </PastClassDetailVideoBlock>
        </>
      )}
    </Modal>
  );
};

export { PastClassDetailModal };
